import { EXCEEDANCE_RESOLUTION_TYPE_LABELS } from "encamp-shared/src/constants/generatorDiscrepancyDefaults";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { getFieldLabel } from "routes/Customer/Waste/Manifests/DiscrepancyReviewDialog.helpers";

/**
 * The shape of a manifest correction as both the drawer's linked list and the
 * mark-resolved picker consume it. Structural rather than the derived GraphQL
 * type so either surface can select a subset.
 */
export type ManifestCorrectionRow = {
  id: string;
  processedManifestId: string;
  manifestTrackingNumber: string;
  shippedDate?: string | null;
  /** Manifest line the corrected field is on; null for a manifest-level field. */
  wasteLineNumber?: string | null;
  field: string;
  /** Where the correction is in its own lifecycle. */
  status: string;
  /** Which side was corrected — EMANIFEST or VENDOR_DATA. Null until it lands. */
  resolutionPath?: string | null;
  eManifestValue?: string | null;
  vendorValue?: string | null;
  resolvedAt?: string | null;
  resolvedByName?: string | null;
};

/**
 * True once a correction has landed: terminal AND we know which side moved. The
 * client mirror of `isCorrectionLanded` in encamp-waste-data-access — it decides
 * whether picking this row resolves the exceedance or parks it.
 */
export function isCorrectionLanded(row: Pick<ManifestCorrectionRow, "status" | "resolutionPath">) {
  return (row.status === "RESOLVED" || row.status === "IGNORED") && row.resolutionPath != null;
}

/**
 * True when a correction closed with no attributable side — a dismissal, or a
 * convergence the pipeline couldn't pin on either source. Neither is evidence the
 * manifest data moved, so an exceedance citing it stays parked and the drawer
 * says so instead of silently waiting forever.
 */
export function isCorrectionClosedUnattributed(
  row: Pick<ManifestCorrectionRow, "status" | "resolutionPath">
) {
  return (row.status === "RESOLVED" || row.status === "IGNORED") && row.resolutionPath == null;
}

/** Where a correction stands, as the chip text the picker and drawer table render. */
export function correctionStateLabel(
  row: Pick<ManifestCorrectionRow, "status" | "resolutionPath">
): string {
  if (isCorrectionLanded(row)) return "Corrected";
  if (isCorrectionClosedUnattributed(row)) return "Closed, unattributed";
  if (row.status === "PENDING") return "In progress";
  return "Open";
}

/** Human label for the corrected discrepancy field. */
export function correctionFieldLabel(field: string): string {
  return getFieldLabel(field);
}

/**
 * A corrected value for display. An empty string is a real outcome (the field was
 * cleared), so it reads as "(blank)" rather than an em dash that would look like
 * missing data.
 */
export function correctionValueLabel(value: string | null | undefined): string {
  if (value == null) return "—";
  return value.trim() || "(blank)";
}

/**
 * Which manifest line a correction is on, or "Manifest" for a manifest-level
 * field. Several lines on one manifest can carry the same field discrepancy, and
 * this is what tells those rows apart.
 */
export function correctionLineLabel(row: Pick<ManifestCorrectionRow, "wasteLineNumber">): string {
  return row.wasteLineNumber ? `Line ${row.wasteLineNumber}` : "Manifest";
}

/**
 * Short US date for an instant in the viewer's zone, matching the dashboard's
 * other timestamps; blank when there's no date. Manifest-correction dates do NOT
 * use this: they render in UTC via getUtcDisplayDate so they read the same here as
 * on the Manifest Review page.
 */
export function formatDay(value: string | null | undefined): string {
  if (!value) return "";
  // ast-grep-ignore: no-local-date-formatting-ts
  return new Date(value).toLocaleDateString("en-US");
}

// Widened so an unrecognized enum member falls through to prettyPrintEnumValue
// rather than failing to compile against the literal key union.
const RESOLUTION_TYPE_LABELS: Record<string, string> = EXCEEDANCE_RESOLUTION_TYPE_LABELS;

/** Display label for how an exceedance was resolved; null while it is still open. */
export function resolutionMethodLabel(resolutionType: string | null | undefined): string | null {
  if (!resolutionType) return null;
  return RESOLUTION_TYPE_LABELS[resolutionType] ?? prettyPrintEnumValue(resolutionType);
}

/**
 * Which side of the manifest was corrected. Both can account for an exceedance:
 * the calculated status is the max across sources, so the correction that
 * matters is the one on whichever source drove it.
 */
export function correctionPathLabel(resolutionPath: string | null | undefined): string {
  // Blank rather than an em dash: an in-flight correction has no corrected side
  // yet, which is not the same as a value we failed to find.
  if (!resolutionPath) return "";
  if (resolutionPath === "EMANIFEST") return "e-Manifest";
  if (resolutionPath === "VENDOR_DATA") return "Vendor data";
  return prettyPrintEnumValue(resolutionPath);
}
